import { useEffect, useRef, useState } from 'react';

// Scroll-triggered entrance.
//
// Deliberately CSS transitions rather than a JS tween library. A JS tween
// writes inline opacity every frame, so if the animation frame loop stalls —
// a backgrounded tab, a low-end phone under load, the page still parsing —
// the element is stranded at whatever opacity it reached and the content is
// simply invisible. A class toggle cannot strand: the browser applies the end
// state whether or not it ever paints an intermediate frame. On the phones
// most of our candidates apply from, that failure mode is not hypothetical.
//
// A timeout is the second belt, so nothing stays hidden because an observer
// never reported (an element that never intersects, a browser quirk).
//
// `from` picks the direction: 'up' (default), 'left', 'right', 'scale'.
const OFFSET = {
  up: 'translate-y-8',
  left: '-translate-x-7',
  right: 'translate-x-7',
  scale: 'scale-95',
};

export default function Reveal({
  children, delay = 0, from = 'up', className = '', as: Tag = 'div',
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || typeof IntersectionObserver === 'undefined') { setShown(true); return undefined; }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        setShown(true);
        observer.unobserve(el);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    observer.observe(el);

    // Failsafe: never leave content hidden because an observer didn't fire.
    const failsafe = setTimeout(() => setShown(true), 2000);

    return () => { observer.disconnect(); clearTimeout(failsafe); };
  }, []);

  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: shown ? `${delay}s` : '0s' }}
      className={[
        'transition-all duration-700 ease-out motion-reduce:transition-none',
        shown ? 'opacity-100 translate-x-0 translate-y-0 scale-100' : `opacity-0 ${OFFSET[from] || OFFSET.up}`,
        className,
      ].join(' ')}
    >
      {children}
    </Tag>
  );
}
